import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchStaffItemList } from "@/api/guide";
import type { StaffItem } from "@/api/guide";

/** 排序选项 */
// const sortOpts = ["评分↓", "价格↑", "价格↓"];

/** 分类选项类型 */
interface CateOption {
  value: number;
  label: string;
}

/** 主题色 */
const THEME = {
  primary: "#FF6B35",
  primaryLight: "#FFF0EB",
  primaryBorder: "#FFD9CE",
  text: "#374151",
  textSecondary: "#9CA3AF",
  textLight: "#D1D5DB",
  star: "#F59E0B",
  success: "#22C55E",
};

/** Mock 服务分类选项（兜底，实际从数据动态提取） */
const FALLBACK_CATE_OPTIONS: CateOption[] = [
  { value: 0, label: "全部" },
];

export default function GuideListPage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isReplaceMode = (routerLocation.state as { replace?: boolean })?.replace === true;

  // UI 状态
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // 筛选状态
  const [serviceFilter, setServiceFilter] = useState(0);
  // const [activeSort, setActiveSort] = useState(0);
  // 动态分类选项（从数据中提取，只增不减）
  const [cateOptions, setCateOptions] = useState<CateOption[]>(FALLBACK_CATE_OPTIONS);
  // 记录已提取的分类 ID→名称 映射（用 ref 避免循环依赖）
  const cateMapRef = useRef<Map<number, string>>(new Map([[0, "全部"]]));

  // 选中的位置信息（从 location-select 返回）
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [selectedAddress, setSelectedAddress] = useState<string>("");

  // 数据状态 - 从 API 获取
  const [guideList, setGuideList] = useState<StaffItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // 监听从 location-select 返回的 state 数据
  const stateFromSelect = routerLocation.state as Record<string, unknown> | null;

  /** 获取达人列表 */
  const fetchList = useCallback(async (category_id?: number) => {
    try {
      setLoading(true);
      const data = await fetchStaffItemList({ page: 1, limit: 50, category_id: category_id ?? serviceFilter });
      setGuideList(data.list);
      setTotal(data.count);

      // 动态提取分类选项（合并策略：只增不减，保留所有已发现的分类）
      let newCateAdded = false;
      for (const item of data.list) {
        if (item.cate_id && item.cate_name && !cateMapRef.current.has(item.cate_id)) {
          cateMapRef.current.set(item.cate_id, item.cate_name);
          newCateAdded = true;
        }
      }
      if (newCateAdded) {
        const options: CateOption[] = Array.from(cateMapRef.current.entries()).map(([id, name]) => ({
          value: id,
          label: name,
        }));
        setCateOptions(options);
      }
    } catch (e) {
      console.error("[guides] 获取达人列表失败:", e);
    } finally {
      setLoading(false);
    }
  }, [serviceFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // serviceFilter 变化时自动重新请求
  useEffect(() => {
    fetchList(serviceFilter);
  }, [serviceFilter, fetchList]);

  useEffect(() => {
    if (stateFromSelect?.selectedCity) {
      const city = String(stateFromSelect.selectedCity);
      const township = stateFromSelect.selectedTownship ? String(stateFromSelect.selectedTownship) : "";
      setSelectedCity(city);
      setSelectedAddress(township ? `${city} · ${township}` : city);
    }
  }, [stateFromSelect?.selectedCity, stateFromSelect?.selectedTownship]);

  /** 根据排序筛选数据 */
  // const sortedList = (() => {
  //   let list = [...guideList];
  //   switch (activeSort) {
  //     case 0:
  //       list.sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
  //       break;
  //     case 1:
  //       list.sort((a, b) => getMinPrice(a) - getMinPrice(b));
  //       break;
  //     case 2:
  //       list.sort((a, b) => getMinPrice(b) - getMinPrice(a));
  //       break;
  //   }
  //   return list;
  // })();
  const sortedList = guideList;

  /** 获取达人最低服务价格（当前接口暂无价格数据，返回0） */
  // const getMinPrice = (_item: StaffItem): number => {
  //   return 0;
  // };

  // /** 获取展示价格 - 当前接口暂无价格字段，已移除 */
  // const getDisplayPrice = (_item: StaffItem): string => {
  //   return "0";
  // };

  const handleBook = (id: number) => {
    if (isReplaceMode) {
      setSelectedId(id);
      setShowConfirm(true);
    } else {
      navigate("/guide-detail-product", { state: { guideId: id } });
    }
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    navigate("/ai-plan");
  };

  return (
    <div className="min-h-screen pb-10" style={{ background: "#F8F9FC", fontFamily: "'Inter','SF Pro Text',sans-serif" }}>
      {/* Header */}
      <div className="bg-white sticky top-0 z-40" style={{ boxShadow: "0 1px 0 #F3F4F6" }}>
        <div className="px-4 pb-3 flex items-center gap-3"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
            style={{ background: "#F3F4F6" }}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: 20, color: THEME.text }} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold" style={{ color: THEME.text }}>达人导游</h1>
            <div className="text-xs mt-0.5 truncate" style={{ color: THEME.textSecondary }}>
              {selectedCity ? `当前城市：${selectedCity}` : "专业服务、品质保障"}
            </div>
          </div>
          {/* 搜索框 / 已选地址展示 - 点击跳转位置选择页 */}
          <div
            className="flex items-center gap-1.5 px-3 py-2 rounded-full cursor-pointer flex-shrink-0 overflow-hidden"
            style={{
              background: selectedAddress ? THEME.primaryLight : "#F3F4F6",
              maxWidth: 160,
            }}
            onClick={() => navigate("/location-select", { state: { from: "/guides" } })}
          >
            <i
              className={selectedAddress ? "ri-map-pin-line" : "ri-search-line"}
              style={{ fontSize: 15, color: selectedAddress ? THEME.primary : THEME.textSecondary }}
            />
            <span
              className="text-xs whitespace-nowrap truncate"
              style={{ color: selectedAddress ? THEME.primary : THEME.textSecondary }}
            >
              {selectedAddress || "搜索达人/地区"}
            </span>
            {selectedAddress && (
              <i className="ri-arrow-right-s-line" style={{ fontSize: 12, color: THEME.primary }} />
            )}
          </div>
        </div>

        {/* 服务分类 */}
        <div className="px-4 pb-2">
          <div className="text-xs font-medium mb-1.5" style={{ color: THEME.textSecondary }}>服务分类</div>
          <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {cateOptions.map((s) => (
              <button
                key={`${s.value}-${s.label}`}
                onClick={() => setServiceFilter(s.value)}
                className="flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
                style={{
                  background: serviceFilter === s.value ? THEME.primary : "#FFFFFF",
                  color: serviceFilter === s.value ? "#FFFFFF" : THEME.text,
                  border: `1px solid ${serviceFilter === s.value ? THEME.primary : "#E5E7EB"}`,
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sort Tabs - 暂时注释掉评分/价格筛选 */}
        {/* <div className="flex border-t" style={{ borderColor: "#F3F4F6" }}>
          {sortOpts.map((s, i) => (
            <button
              key={s}
              onClick={() => setActiveSort(i)}
              className="flex-1 py-2.5 text-xs font-medium cursor-pointer whitespace-nowrap transition-colors"
              style={{
                color: activeSort === i ? THEME.primary : THEME.textSecondary,
                borderBottom: activeSort === i ? `2px solid ${THEME.primary}` : "2px solid transparent",
              }}
            >
              {s}
            </button>
          ))}
        </div> */}

        {isReplaceMode && (
          <div className="mx-4 my-2 px-3 py-2 rounded-xl flex items-center gap-2" style={{ background: "#FEF3C7" }}>
            <i className="ri-information-line" style={{ color: "#D97706", fontSize: 15 }} />
            <span className="text-xs text-amber-700">当前为替换模式，选择后将更新AI套餐中的导游</span>
          </div>
        )}
      </div>

      {/* Count */}
      <div className="px-4 pt-3 pb-1">
        <span className="text-xs" style={{ color: THEME.textSecondary }}>
          共 <span className="font-semibold" style={{ color: THEME.text }}>{total}</span> 位达人导游
        </span>
      </div>

      {/* Guide list */}
      <div className="px-4 py-2 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <span className="text-sm" style={{ color: THEME.textSecondary }}>加载中...</span>
          </div>
        ) : sortedList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <i className="ri-user-line" style={{ fontSize: 40, color: THEME.textLight }} />
            <span className="text-sm mt-2" style={{ color: THEME.textSecondary }}>暂无达人数据</span>
          </div>
        ) : sortedList.map((guide) => {
          const isOnline = guide.work_status === 1;
          return (
          <div
            key={guide.id}
            className="bg-white rounded-2xl p-4 cursor-pointer transition-all active:scale-[0.98]"
            style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
            onClick={() => handleBook(guide.id)}
          >
            {/* 头部：头像 + 信息 */}
            <div className="flex gap-3">
              {/* 头像 - 圆形 */}
              <div className="relative flex-shrink-0">
                <img
                  src={guide.avatar}
                  alt={guide.nickname}
                  className="w-[68px] h-[68px] rounded-full object-cover object-top"
                />
                {/* 认证角标 */}
                <div
                  className="absolute bottom-0 right-0 w-5 h-5 flex items-center justify-center rounded-full"
                  style={{ background: THEME.primary }}
                >
                  <i className="ri-star-fill" style={{ color: "#FFF", fontSize: 10 }} />
                </div>
              </div>

              {/* 右侧信息 */}
              <div className="flex-1 min-w-0">
                {/* 第一行：姓名 + 标签 */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-bold" style={{ color: THEME.text }}>{guide.nickname}</span>
                  {guide.served_count > 0 && (
                    <span className="flex items-center gap-0.5 text-xs" style={{ color: THEME.primary }}>
                      <i className="ri-heart-3-fill" style={{ fontSize: 12 }} />
                      已服务{guide.served_count}
                    </span>
                  )}
                  {/* 推荐标签 */}
                  <span
                    className="px-1.5 py-0.5 rounded text-xs"
                    style={{ color: THEME.primary, border: `1px solid ${THEME.primaryBorder}`, background: THEME.primaryLight }}
                  >
                    推荐
                  </span>
                </div>

                {/* 第二行：星级 + 城市 + 已服务 */}
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-0.5">
                    <i className="ri-star-fill" style={{ color: THEME.star, fontSize: 13 }} />
                    <span className="text-xs font-semibold" style={{ color: THEME.text }}>{guide.score || "-"}</span>
                  </div>
                  <span className="text-xs" style={{ color: THEME.textSecondary }}>{guide.city_name || "未知城市"}</span>
                </div>

                {/* 第三行：技能标签 - 橙色边框 */}
                {guide.service_label?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {guide.service_label.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded text-xs"
                      style={{ color: THEME.primary, border: `1px solid ${THEME.primaryBorder}`, background: "#FFF" }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                )}

                {/* 第四行：介绍文字 */}
                {guide.introduction && (
                  <div
                    className="mt-2 text-xs line-clamp-2"
                    style={{ color: THEME.textSecondary }}
                  >
                    {guide.introduction}
                  </div>
                )}
              </div>
            </div>

            {/* 底部操作栏：接单状态 + 价格 + 按钮 */}
            <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "1px solid #F3F4F6" }}>
              {/* 左侧：价格占位 */}
              <div className="flex items-center gap-3" />

              {/* 右侧：预约按钮 */}
              <button
                onClick={(e) => { e.stopPropagation(); handleBook(guide.id); }}
                className="px-5 py-1.5 rounded-full text-xs font-semibold text-white cursor-pointer whitespace-nowrap transition-all active:scale-95"
                style={{
                  background: isOnline ? THEME.primary : "#D1D5DB",
                }}
                disabled={!isOnline}
              >
                预约服务
              </button>
            </div>
          </div>
        );
        })}
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: "rgba(0,0,0,0.4)" }}>
          <div className="bg-white rounded-t-3xl w-full p-6" style={{ maxWidth: 480 }}>
            <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-5" />
            <div className="text-lg font-bold text-gray-800 mb-2">替换导游？</div>
            <div className="text-sm text-gray-500 mb-6">将替换为达人「{sortedList.find(g => g.id === selectedId)?.nickname}」，套餐价格将同步更新。</div>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirm(false)} className="flex-1 py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap" style={{ background: "#F3F4F6", color: "#6B7280" }}>取消</button>
              <button onClick={handleConfirm} className="flex-1 py-3 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap" style={{ background: THEME.primary }}>确认替换</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
